import {
  Button,
  ButtonsGroup,
  Checkbox,
  Flex,
  Modal,
  ModalProps,
  notify,
  Space,
  Text,
  useModal,
} from '@billy/design-system'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useVatDeclaration } from '@features/vatDeclarations/hooks/useVatDeclaration'

import { useSegment } from '../../../../hooks'
import { usePdfExport } from '../VatDeclaration/elements/VatDeclarationReport/hooks/usePdfExport'
import { useSpreadsheetExport } from '../VatDeclaration/elements/VatDeclarationReport/hooks/useSpreadsheetExport'
import { ExportModalCheckboxGroup } from './elements/ExportModalCheckboxGroup'
import { ExportExtensionsTypes } from './types/exportExtensionsTypes'

interface VatDeclarationsExportModalProps extends ModalProps {
  period?: string
  vatDeclarationId: string
}

export const VatDeclarationsExportModal = ({ id, period, vatDeclarationId }: VatDeclarationsExportModalProps) => {
  const { close } = useModal(id)
  const { t } = useTranslation()
  const { track } = useSegment()
  const [includeAuditTrail, setIncludeAuditTrail] = useState(false)

  const [selectedExportExtensions, setSelectedExportExtensions] = useState<ExportExtensionsTypes[]>([])

  const { vatDeclaration, isLoading: isVatDeclarationLoading } = useVatDeclaration({ vatDeclarationId })
  const { exportAsPdf, isLoading: isPdfLoading } = usePdfExport({ vatDeclaration, withAuditTrail: includeAuditTrail })
  const { exportAsXlsx, exportAsCsv, isXlsxLoading, isCsvLoading } = useSpreadsheetExport({
    vatDeclaration,
    withAuditTrail: includeAuditTrail,
  })

  const isLoading = isVatDeclarationLoading || isPdfLoading || isXlsxLoading || isCsvLoading

  const handleMutate = useCallback(() => {
    if (selectedExportExtensions.length > 0) {
      for (const format of selectedExportExtensions) {
        try {
          if (format === 'pdf') {
            exportAsPdf()
          }
          if (format === 'xlsx') {
            exportAsXlsx()
          }
          if (format === 'csv') {
            exportAsCsv()
          }

          /* eslint-disable @typescript-eslint/naming-convention */
          track('xxx - Mikkel - Exports', {
            export_type: 'VAT settlement',
            file_format: format,
            period,
          })
          /* eslint-enable @typescript-eslint/naming-convention */
        } catch {
          notify({
            id: format,
            message: t('vat_declarations.export_modal.error', { format }),
            variant: 'error',
          })
        }
      }
    }
  }, [exportAsCsv, exportAsPdf, exportAsXlsx, period, selectedExportExtensions, t, track])

  return (
    <Modal closable id={id} size="s">
      <Modal.Header>
        <Text variant="h2">{t('vat_declarations.export_modal.title')}</Text>
      </Modal.Header>
      <Modal.Body>
        <Text>{t('vat_declarations.export_modal.body.helper_text')}</Text>
        <Space size="s24" />
        <Text variant="bodyLG" weight="medium">
          {t('vat_declarations.export_modal.body.text')}
        </Text>
        <Space size="s8" />
        <Flex>
          <Space size="s8" horizontal />
          <ExportModalCheckboxGroup
            selectedExportExtensions={selectedExportExtensions}
            setSelectedExportExtensions={setSelectedExportExtensions}
          />
        </Flex>

        <Space size="s24" />

        <Text variant="bodyLG" weight="medium">
          {t('vat_declarations.export_modal.body.audit_trail')}
        </Text>
        <Space size="s8" />
        <Flex>
          <Space size="s8" horizontal />
          <Checkbox checked={includeAuditTrail} onChange={() => setIncludeAuditTrail((prev) => !prev)}>
            <Text>{t('vat_declarations.export_modal.checkbox.audit_trail')}</Text>
          </Checkbox>
        </Flex>
      </Modal.Body>

      <Modal.Footer>
        <ButtonsGroup>
          <Button variant="text" onClick={close}>
            {t('cancel')}
          </Button>
          <Button variant="filled" onClick={handleMutate} disabled={isLoading} loading={isLoading}>
            {t('vat_declarations.export_modal.footer.button.export')}
          </Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}
